import {useLocation, useNavigate} from "react-router-dom"
import type {NavigateOptions} from "react-router-dom"

/**
 * Location state key used when navigating from a list to a detail page.
 * The list page should pass `state: { listPath: location.pathname + location.search }`
 * so that the detail page's breadcrumb "back to list" preserves pagination and filters.
 */
export const LIST_PATH_STATE_KEY = "listPath" as const

function isRecord(obj: unknown): obj is Record<string, unknown> {
	return obj !== null && typeof obj === "object"
}

function getListPath(state: unknown): string | null {
	if (!isRecord(state)) return null
	const value = state[LIST_PATH_STATE_KEY]
	return typeof value === "string" && value.length > 0 ? value : null
}

/**
 * Returns the URL to use for "back to list" breadcrumb link.
 * When the user navigated from a list (e.g. with pagination/filters in the URL),
 * that full URL is stored in location state so returning via breadcrumb restores position.
 *
 * @param defaultPath - Path to use when no list path was passed (e.g. "/customers/")
 */
export function useListReturnPath(defaultPath: string): string {
	const location = useLocation()
	const listPath = getListPath(location.state)
	return listPath ?? defaultPath
}

/** List URL for a resource-detail route like `/check-deposits/:id/assignments` → `/check-deposits#tab=Assignments`. */
export function pageFallbackPath(pathname: string, hash = "#tab=Assignments"): string {
	return `/${pathname.split("/").filter(Boolean)[0] ?? ""}${hash}`
}

/**
 * Returns a navigate function that automatically includes the current list URL
 * (pathname + search) in location state as `listPath`, so the detail page
 * breadcrumb can return the user to the same page/filters/pagination.
 * Use this when navigating from a list view to a detail view.
 */
export function useNavigateWithListPath(): (to: string, options?: NavigateOptions) => void {
	const navigate = useNavigate()
	return (to: string, options?: NavigateOptions) => {
		// Use window.location so we capture the actual URL (including params set by use-location-state
		// via history.replaceState), which React Router's useLocation() may not have updated yet.
		const listPath = window.location.pathname + window.location.search + (window.location.hash || "")
		const existingState = options && isRecord(options.state) ? options.state : {}
		navigate(to, {
			...options,
			state: {...existingState, [LIST_PATH_STATE_KEY]: listPath},
		})
	}
}
